import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";

interface AddOnboardingTaskSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onboardingId?: Id<"clientOnboarding">;
  onTaskAdded?: () => void;
}

export function AddOnboardingTaskSheet({
  isOpen,
  onClose,
  onboardingId,
  onTaskAdded,
}: AddOnboardingTaskSheetProps) {
  // State for form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [priority, setPriority] = useState("medium"); // Default to medium priority
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch organization members
  const members = useQuery(api.onboardingTasks.getOrgMembers) || [];

  // Create task mutation
  const createTask = useMutation(api.onboardingTasks.createTask);

  // Handle form submission
  const handleSubmit = async () => {
    if (!title || !dueDate || !assignedTo || !onboardingId) return;

    setIsSubmitting(true);
    try {
      await createTask({
        onboardingId,
        title,
        description: description || undefined,
        dueDate,
        assignedTo: assignedTo as Id<"users">,
        priority: priority as "low" | "medium" | "high",
      });

      // Reset form and close sheet
      setTitle("");
      setDescription("");
      setDueDate("");
      setAssignedTo("");
      setPriority("medium");

      toast({
        title: "Task created successfully!",
        description: "The new task has been added to the onboarding process.",
      });

      if (onTaskAdded) onTaskAdded();
      onClose();
    } catch (error) {
      console.error("Error creating onboarding task:", error);
      toast({
        title: "Failed to create task",
        description: "Please try again or check your connection.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:max-w-[400px]">
        <SheetHeader>
          <SheetTitle>Add Onboarding Task</SheetTitle>
          <SheetDescription>Create a new task for this onboarding process.</SheetDescription>
        </SheetHeader>
        <div className="space-y-6 py-6">
          <div className="space-y-2">
            <Label htmlFor="title">Task Title</Label>
            <Input
              id="title"
              placeholder="Enter task title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Enter task description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="due-date">Due Date</Label>
            <Input
              id="due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assigned">Assigned To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger>
                <SelectValue placeholder="Select team member" />
              </SelectTrigger>
              <SelectContent>
                {members.length === 0 ? (
                  <SelectItem value="loading" disabled>
                    No team members found
                  </SelectItem>
                ) : (
                  members.map((member: any) => (
                    <SelectItem key={member._id} value={member._id}>
                      {member.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={isSubmitting || !title || !dueDate || !assignedTo}
          >
            {isSubmitting ? "Creating..." : "Create Task"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
